import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { Color } from '../../../constants/color.constants';
import { Orientation } from '../../../constants/orientation.constants';

/**
 * LSQ Dropdown
 *
 * @description Renders a dropdown list from an array of items with the shape `{ label: string; value: any }`.
 * @export
 * @class LsqDropdownComponent
 * @property {boolean} show A boolean defining whether the dropdown is shown. Supports two-way binding (e.g. `[(show)]="myBoolean"`).
 * @property {'left'|'right'|'center'|false|number|{left: string}|{right: string}} [triangle] Tells the component where to display the triangle above the list. One of `'left'`, `'right'`, `'center'`, `false` (if no triangle should be displayed) or a number of `rem`s from the left edge of the list, or an `ngStyle`-ish object defining the `left` or `right` css property (defaults to `'left'`).
 * @property {'dark'|'light'} [background] Defines a 'dark' or 'light' background (defaults to `'dark'`)
 * @property {string} [header] If not undefined, displays text in a header bar.
 * @property {EventEmitter<boolean>} showChange Emits a boolean representing whether the dropdown is showing.
 */
@Component({
    selector: 'lsq-dropdown',
    template: `
        <div
            *ngIf="isShowing"
            [@dropdown]="animateState"
            class="lsq-dropdown-container"
            [ngClass]="[hasHeaderClass, parentClass]"
        >
            <span
                *ngIf="showTriangle"
                [ngClass]="[triangleClass, 'dropdown-triangle', lightTriangle ? 'light' : '']"
            ></span>
            <div class="lsq-dropdown-content">
                <ng-content></ng-content>
            </div>
        </div>
    `,
    animations: [
        trigger('dropdown', [
            transition('void => slideRight', [
                animate(
                    200,
                    keyframes([
                        style({ transform: 'translateX(-25%)', opacity: 0, offset: 0 }),
                        style({ transform: 'translateX(15px)', opacity: 0.25, offset: 0.7 }),
                        style({ transform: 'translateX(0%)', opacity: 1, offset: 1.0 })
                    ])
                )
            ]),
            transition('void => slideLeft', [
                animate(
                    200,
                    keyframes([
                        style({ transform: 'translateX(25%)', opacity: 0, offset: 0 }),
                        style({ transform: 'translateX(-15px)', opacity: 0.25, offset: 0.7 }),
                        style({ transform: 'translateX(0%)', opacity: 1, offset: 1.0 })
                    ])
                )
            ]),
            transition('void => slideDown', [
                animate(
                    200,
                    keyframes([
                        style({ transform: 'translateY(-25%)', opacity: 0, offset: 0 }),
                        style({ transform: 'translateY(15px)', opacity: 0.25, offset: 0.7 }),
                        style({ transform: 'translateY(0%)', opacity: 1, offset: 1.0 })
                    ])
                )
            ])
        ])
    ]
})
export class LsqDropdownComponent implements OnInit {
    public isShowing = false;
    public showTriangle = true;
    public triangleLeftPos: string;
    public triangleRightPos: string;
    public triangleMarginLeft: string;
    public triangleClass: 'left' | 'right' | 'center';
    public color = Color;
    public animateState?: string;

    @Input() public triangle?:
        | 'left'
        | 'right'
        | 'center'
        | false
        | number
        | { left?: string; right?: string } = Orientation.Left;
    @Input() public hasHeader = false;
    @Input() public headerContext?: any;
    @Input() public headerChangeStream?: Observable<{ runChangeDetectionOnly?: boolean }>;
    @Input() public headerIsButton = false;
    @Input() public parentClass = '';
    @Input() public shouldAnimate = false;
    @Input() public lightTriangle = false;

    @Output() public showChange = new EventEmitter<boolean>();
    @Output() public headerClick = new EventEmitter<undefined>();

    @Input()
    public get show(): boolean {
        return this.isShowing;
    }
    public set show(val: boolean) {
        this.isShowing = val;
        this.showChange.emit(this.isShowing);
    }

    public ngOnInit() {
        if (this.triangle) {
            if (
                this.triangle === Orientation.Left ||
                this.triangle === Orientation.Right ||
                this.triangle === Orientation.Center
            ) {
                this.triangleClass = <'left' | 'right' | 'center'>this.triangle;
            }
            if (typeof this.triangle === 'number') {
                this.triangleLeftPos = this.triangle.toString() + 'rem';
            }
            if (this.triangle && typeof this.triangle === 'object') {
                if (this.triangle.left) {
                    this.triangleLeftPos = this.triangle.left;
                }
                if (this.triangle.right) {
                    this.triangleRightPos = this.triangle.right;
                }
            }
        } else {
            this.showTriangle = false;
        }

        if (this.shouldAnimate) {
            if (this.triangle === Orientation.Left) {
                this.animateState = 'slideRight';
            } else if (this.triangle === Orientation.Right) {
                this.animateState = 'slideLeft';
            } else if (this.triangle === Orientation.Center) {
                this.animateState = 'slideDown';
            }
        }
    }

    public get hasHeaderClass(): string {
        return this.hasHeader ? 'has-header' : '';
    }

    public handleHeaderClick() {
        if (this.headerIsButton) {
            this.headerClick.emit();
        }
    }
}
